import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Form } from '@hrlogics/react-components/form';

import { appRoutes, DEFAULT_VALUE_IF_NIL, RouterEntitiesId } from '@clearverify/constants';
import { parseNullableValue } from '@clearverify/utils';

import {
  ActiveInactive,
  InboxItemsScrollableListDocument,
  useCreateReverificationRequestMutation,
} from 'src/api';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useEmployeeInfoForVerificationRequest } from 'src/hooks/employee';
import { useInboxItemsQueryVariables } from 'src/hooks/inboxItem';
import { usePermissiblePurpose } from 'src/hooks/permissiblePurposes';
import {
  useVerificationRequest,
  useVerificationRequestFormData,
} from 'src/hooks/verificationRequest';
import { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

import { FreezeAccountBlock, NewRequestBlock, PaymentBlock } from './components';
import { ReverificationSubmitFormValues } from './types';
import { reverificationSubmitFormValidationSchema } from './utils';

type ReverificationSubmitByVerifierStaffModalFormProps = {
  inboxItemId: string;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
};

export const ReverificationSubmitByVerifierStaffModalForm: React.FC<
  ReverificationSubmitByVerifierStaffModalFormProps
> = ({ inboxItemId, closeModal, switchClosability }) => {
  const navigate = useNavigate();

  const { isMobile } = useScreenInfo();
  const { createApolloContext, spawnNotification } = useNotification();

  const [needToRedirect, setNeedToRedirect] = React.useState(false);

  const inboxItemsQueryVariables = useInboxItemsQueryVariables();

  const { isTotalChargeLoading } = useVerificationRequestFormData();

  const { verificationRequest } = useVerificationRequest({ inboxItemId });
  const { permissiblePurpose } = usePermissiblePurpose({
    id: verificationRequest?.permissiblePurposeId,
  });
  const { employeeInfoForVerificationRequest } = useEmployeeInfoForVerificationRequest(
    verificationRequest?.employeeId,
  );

  const isEmployeeFrozen = employeeInfoForVerificationRequest?.freeze === ActiveInactive.Active;

  const [createReverificationRequest] = useCreateReverificationRequestMutation({
    context: createApolloContext({
      errorMessage: 'Error creating verification request',
    }),
    refetchQueries: [
      { query: InboxItemsScrollableListDocument, variables: inboxItemsQueryVariables },
    ],
  });

  const handleSubmit = React.useCallback(
    async (formValues: ReverificationSubmitFormValues) => {
      switchClosability(false);

      let success = false;
      let inboxItemIdToRedirect: string | null = null;

      try {
        const { data } = await createReverificationRequest({
          variables: {
            inboxItemId,
            paymentMethodId: formValues.paymentMethodId,
          },
        });

        const redirectUrl = data?.createReverificationRequest.redirectUrl;
        if (redirectUrl) {
          setNeedToRedirect(true);
          window.location.replace(redirectUrl);
          return;
        }

        success = true;

        if (data?.createReverificationRequest.inboxItemId) {
          inboxItemIdToRedirect = data.createReverificationRequest.inboxItemId;
        }

        spawnNotification('Reverification successfully created', 'success');
      } finally {
        switchClosability(true);
        if (success) {
          closeModal();
        }
      }

      if (inboxItemIdToRedirect) {
        navigate(
          buildUrl(appRoutes.inbox.inboxItem, {
            [RouterEntitiesId.inboxItem]: inboxItemIdToRedirect,
          }),
        );
      }
    },
    [
      switchClosability,
      createReverificationRequest,
      inboxItemId,
      spawnNotification,
      closeModal,
      navigate,
    ],
  );

  return (
    <React.Fragment>
      {isMobile && (
        <Typography variant="subtitle1">Confirm you want to submit reverification</Typography>
      )}

      <Grid container spacing={1} mt={1}>
        <Grid size={{ xs: 6 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'} sx={textStyles}>
            Employee Full Name:
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'}>
            {parseNullableValue(employeeInfoForVerificationRequest?.fullName)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'} sx={textStyles}>
            Report Type:
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'}>
            {verificationRequest?.type
              ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type]
              : DEFAULT_VALUE_IF_NIL}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'} sx={textStyles}>
            Permissible Purpose:
          </Typography>
          <Typography variant={isMobile ? 'body2' : 'body1'} mt={0.5}>
            {parseNullableValue(permissiblePurpose?.permissiblePurposeReference.label)}
          </Typography>
        </Grid>
      </Grid>

      <Form<ReverificationSubmitFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ valid: true, submitting: true }}
        validationSchema={reverificationSubmitFormValidationSchema}
      >
        {({ submitting, valid }) => (
          <React.Fragment>
            <PaymentBlock
              inboxItemId={inboxItemId}
              employeeId={verificationRequest?.employeeId}
              disabled={submitting}
            />

            <FreezeAccountBlock employeeId={verificationRequest?.employeeId} />

            <NewRequestBlock disabled={submitting} employeeId={verificationRequest?.employeeId} />

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                type="button"
                variant="outlined"
                color="infoButton"
                onClick={() => closeModal()}
                disabled={submitting || needToRedirect}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={
                  !valid || submitting || isTotalChargeLoading || needToRedirect || isEmployeeFrozen
                }
                loading={submitting || isTotalChargeLoading}
              >
                Yes, confirm
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};

const textStyles: SxProp = theme => ({
  opacity: 1,
  [theme.breakpoints.down('sm')]: {
    opacity: 0.6,
  },
});

const initialValues: Partial<ReverificationSubmitFormValues> = {
  paymentMethodId: null,
};
